// What the board doing a thing sounds like.
//
// The one place the game's vocabulary meets the pack's. `src/client/ai/render/` names
// visual moments and knows nothing about mp3s; `src/client/ai/audio/` plays ids and knows
// nothing about chess — so every question of the form "should that be heard,
// and as what" is answered here and nowhere else. A re-cut of the pack is an
// edit to this file and the manifest, with no scene code involved.
//
// Pure and tested for that reason: it is the layer where a wrong answer is a
// game that sounds wrong rather than a game that throws, which nothing else
// would catch.

import type { Shape } from "src/client/ai/audio/engine";
import type { SoundId } from "src/client/ai/audio/sounds";
import type { GameSnapshot } from "src/engine/game/GameStore";
import type { SoundEvent } from "src/client/ai/render/soundEvents";
import { BISHOP, KING, KNIGHT, PAWN, type PieceType, QUEEN, ROOK } from "src/engine/rules/board";
import { isArtifactKind, type StructureKind } from "src/engine/rules/industry/state";

/**
 * At this many squares a move stops being a step and becomes a slide.
 *
 * Chebyshev, so a knight's L and a bishop's diagonal are measured the way the
 * board looks rather than the way it is stored. Four is the user's number: a
 * rook crossing half a classic board is plainly a different gesture from a
 * pawn's push, and everything shorter is a step wherever it lands.
 */
export const LONG_MOVE = 4;

/**
 * How long after a landing the sweep's ore is heard.
 *
 * A king lifting what it lands on happens *with* the move, so the two sounds
 * arrive on the same frame and read as one thick thud. Eighty milliseconds is
 * long enough to be two events and short enough to still be one action.
 */
const SWEEP_DELAY_SEC = 0.08;

/**
 * Each rank's own voice. A monster has none and is heard as the forge itself.
 *
 * `Partial` because the table is honest about the seventh type rather than
 * pointing it at a file that is not about it.
 */
const PIECE_VOICE: Partial<Record<PieceType, SoundId>> = {
  [PAWN]: "pawn",
  [KNIGHT]: "knight",
  [BISHOP]: "bishop",
  [ROOK]: "rook",
  [QUEEN]: "queen",
  [KING]: "king"
};

/** What a piece of this rank sounds like, or nothing if it has no voice. */
export const pieceVoiceOf = (type: PieceType): SoundId | undefined => PIECE_VOICE[type];

/**
 * What a structure of this kind sounds like, or nothing if it is not an
 * artifact: a store is built by a foundry for stone and has no voice, an
 * artifact stood up from a token has one. One voice for every kind —
 * the file is about the occasion rather than the kind.
 */
export const structureVoiceOf = (kind: StructureKind): SoundId | undefined =>
  isArtifactKind(kind) ? "artifact-placed" : undefined;

/**
 * How a piece sounds when it is merely clicked.
 *
 * The same file as its forge — the piece is the piece — but a selection is a
 * glance and a forge is an occasion, and they must not weigh the same: at full
 * weight every look round the board is a fanfare. So it is a third as loud and
 * a fifth quicker, which pitches it up enough to read as the small version of
 * the sound rather than the sound at a distance.
 */
export const SELECT_VOICE: Shape = { gain: 0.3, rate: 1.2 };

/** What joining the hand of tokens sounds like. Not at a square. */
export const ARTIFACT_CLAIMED: SoundId = "artifact-claimed";

/**
 * How many artifact tokens one command just put in the hand.
 *
 * The hand is `run.tokens`, and it grows by every road there is — a crown
 * reaching the king, a station's craft, the dev panel's grant — so the count
 * is watched rather than the roads. **Exactly one command**, or nought: a
 * load replaces the log wholesale and an undo truncates it, and a hand that
 * is fuller after either is not a hand that was just dealt into. The
 * renderer's `played` test, for the renderer's reason.
 */
export function tokensGained(previous: GameSnapshot | undefined, next: GameSnapshot): number {
  if (!previous || next.log.length !== previous.log.length + 1) return 0;
  const before = previous.state.run?.tokens?.length ?? 0;
  const after = next.state.run?.tokens?.length ?? 0;
  return Math.max(0, after - before);
}

/**
 * How a card sounds when it is only being *aimed*.
 *
 * The same file as the play, because it is the same card — but aiming is a
 * promise and playing is the act, and a board lighting up its candidates must
 * not sound like a card being spent, or a cancelled aim reads as a card lost.
 * So it is under half as loud and pitched up, `SELECT_VOICE`'s reasoning for
 * `SELECT_VOICE`'s reason.
 */
export const CARD_AIM_VOICE: Shape = { gain: 0.45, rate: 1.15 };

/** One sound to play. `at` is a square; absent means it is not anywhere. */
export interface SoundCue {
  id: SoundId;
  at?: number;
  delaySec?: number;
}

/** Nothing to hear. Shared, so the common answer allocates nothing. */
const SILENT: readonly SoundCue[] = [];

/**
 * What one visual moment sounds like.
 *
 * A list rather than one cue because a moment may be several sounds and
 * routinely is none — a move setting off is silent unless it is long enough to
 * be a slide.
 */
export function cuesFor(event: SoundEvent): readonly SoundCue[] {
  switch (event.kind) {
    // A slide is heard as it *starts*, because that is when the piece is at
    // `from` — the landing is a separate moment with its own sound.
    case "move-started":
      return event.distance >= LONG_MOVE ? [{ id: "drag", at: event.from }] : SILENT;

    // A capture's landing is the take, and it **replaces** the step rather than
    // joining it: a piece arriving is one event, and two files on one frame is
    // the flam the whole engine is built to avoid.
    case "piece-landed":
      return [{ id: event.capture ? "take" : "step", at: event.at }];

    // Each rank in its own voice; a piece with none is heard as the forge.
    case "minted":
      return [{ id: pieceVoiceOf(event.type) ?? "craft", at: event.at }];

    // Only a token can be placed, so this is always an artifact today; the
    // test is kept because the event says "structure" and means it.
    case "structure-placed": {
      const voice = structureVoiceOf(event.structure);
      return voice ? [{ id: voice, at: event.at }] : SILENT;
    }

    // **One cue per phase, however many ovens lit.** The engine's cooldown
    // would drop the rest anyway; deciding it here is the difference between a
    // policy and a coincidence, and it means the square that is heard is the
    // first one the tick reported rather than whichever won a race.
    case "cook-started":
      return event.squares.length > 0 ? [{ id: "refine", at: event.squares[0] }] : SILENT;

    // Not positional: ground arriving is not at a square, it *is* the squares,
    // and a whole board panned to one ear would be plainly wrong.
    case "cells-rising":
      return [{ id: "generate-board" }];

    // A sweep is at the square the mover landed on; mining is reported without
    // one (`at < 0`), because the squares dug are not in the tick's report and
    // a centroid of them would be a lie. The delay is the sweep's alone — the
    // non-positional case has no landing to sit behind.
    case "collected":
      return event.at >= 0
        ? [{ id: "resources", at: event.at, delaySec: SWEEP_DELAY_SEC }]
        : [{ id: "resources" }];
  }
}
